import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

/**
 * Staff Google OAuth callback.
 * Emergent lands us here with `#session_id=<uuid>` in the URL fragment.
 * We POST it to /api/auth/google-callback which sets the access_token cookie.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const nav = useNavigate();
  const loc = useLocation();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const sessionId = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    if (!sessionId) {
      toast.error("Sign-in session was missing");
      nav("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        await api.post("/auth/google-callback", { session_id: sessionId });
        await refresh?.();
        window.history.replaceState({}, "", loc.pathname);
        toast.success("Signed in with Google");
        nav("/", { replace: true });
      } catch (err) {
        const detail = err?.response?.data?.detail || "Google sign-in failed";
        toast.error(detail);
        nav("/login", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#022C22] text-white">
      <div className="text-center">
        <Loader2 size={28} className="animate-spin text-amber-300 mx-auto" />
        <div className="mt-3 text-sm text-emerald-100/80">Finishing Google sign-in…</div>
      </div>
    </div>
  );
}
